import { themeConfig } from 'src/theme/theme-config';
import { LINE, RADIUS, TEXT_WRAP } from 'src/theme/ui-tokens';

// ----------------------------------------------------------------------

/**
 * **공개 지면 세 곳(홈·기능 소개·요금)의 표현 토큰** — 정본은 여기 하나다.
 *
 * 공개 지면은 흰 바탕에 고정된 마케팅 화면이라 앱 테마의 조밀한 사다리(13.5px 본문)를 그대로 쓰지
 * 않고, 큰 제목·본문 사다리와 구역 여백을 여기서 정한다. 색은 `themeConfig.palette`, 선·모서리는
 * `theme/ui-tokens` 에서 끌어온다 — 강조색은 `primary` 하나뿐이다.
 * ⚠️ 기능 소개·요금은 이 파일을 다시 내보낸다 — 지면마다 값을 다시 적으면 세 지면이 어긋난다.
 */

const PALETTE = themeConfig.palette;

export const CM = {
  primary: PALETTE.primary.main,
  primaryLight: PALETTE.primary.light,
  primaryDark: PALETTE.primary.dark,
  primaryLighter: PALETTE.primary.lighter,
  secondary: PALETTE.secondary.main,
  warning: PALETTE.warning.main,
  error: PALETTE.error.main,
  text: '#1A1A2E',
  textDark: '#111827',
  textMuted: '#5A5A68',
  textSubtle: '#8A8A96',
  textBody: '#4B4B5A',
  /** 덩어리를 두르는 선 — 앱의 `LINE.edge` 와 같은 값 */
  border: LINE.edge,
  borderLight: PALETTE.primary.light,
  bgMuted: PALETTE.grey[100] ?? '#F8F8F8',
  bgSection: PALETTE.grey[50] ?? '#FBFBFB',
  success: PALETTE.secondary.main,
  /** 브랜드 하늘색의 투명도 변주 — 옅은 면·고른 칸의 테두리 */
  alpha: (opacity: number) => `rgba(16, 175, 234, ${opacity})`,
};

/** 좌우 여백 — 폰 16px · 태블릿 32px · 넓은 화면 48px */
export const SECTION_PX = { xs: 2, sm: 4, md: 6 };

/** 구역의 위아래 여백 — 폰 64px · 넓은 화면 96px. 세 지면이 같은 박자로 선다 */
export const SECTION_PY = { xs: 8, md: 12 };

/** 본문 폭 */
export const PUBLIC_MAX_WIDTH = 1120;

/** 랜딩 전체가 한글 낱말을 안 끊게 한다. 조립부가 하위 Typography 에도 물려 준다 */
export const TEXT_KEEP_ALL_SX = { wordBreak: 'keep-all' };

/**
 * 공개 지면의 글자 사다리 — 여섯 칸. 굵기는 제목 700 · 소제목 600 · 본문 400.
 * ⛔ 공개 지면에서 이 밖의 크기를 새로 만들지 않는다(목업 안의 작은 글자는 목업 파일이 정한다).
 * 🔑 줄바꿈 꼴도 칸이 쥔다(`TEXT_WRAP` 의 값) — 공개 지면의 글은 전부 네댓 줄 안쪽의 짧은 글이라
 *   **모든 칸이 줄 길이를 고르게(`balance`)** 한다. `pretty` 는 한 낱말만 떨어지는 것만 막아
 *   「…그대로 / 써 보세요」처럼 두 어절짜리 꼬리가 남는다.
 * ⚠️ `TEXT_KEEP_ALL_SX` 는 낱말을 안 끊는 것만 하고 `textWrap` 을 싣지 않는다 — 조립부가 하위 글자에
 *   물려 주므로 거기 실으면 이 칸들을 덮는다.
 */
export const PUBLIC_TEXT = {
  /** 첫 화면 큰 제목 */
  display: { fontSize: { xs: 30, sm: 40, md: 52 }, fontWeight: 700, lineHeight: 1.2, letterSpacing: '-0.03em', textWrap: TEXT_WRAP.title.textWrap },
  /** 구역 제목 */
  title: { fontSize: { xs: 26, md: 36 }, fontWeight: 700, lineHeight: 1.28, letterSpacing: '-0.02em', textWrap: TEXT_WRAP.title.textWrap },
  /** 카드·항목 제목 */
  subtitle: { fontSize: { xs: 19, md: 22 }, fontWeight: 600, lineHeight: 1.4, letterSpacing: '-0.01em', textWrap: TEXT_WRAP.title.textWrap },
  /** 제목 아래 설명 */
  lead: { fontSize: { xs: 16, md: 18 }, fontWeight: 400, lineHeight: 1.65, textWrap: TEXT_WRAP.title.textWrap },
  /** 본문 */
  body: { fontSize: 15, fontWeight: 400, lineHeight: 1.7, textWrap: TEXT_WRAP.title.textWrap },
  /** 구역 머리 작은 말 */
  eyebrow: { fontSize: 13, fontWeight: 600, letterSpacing: '0.04em', lineHeight: 1.5 },
} as const;

/**
 * **문장 하나를 한 덩이로** — 넓은 폭에서 줄이 문장 사이에서만 갈린다(폰에서는 보통 글처럼 흐른다).
 * 🔑 문장 둘셋짜리 가운데 정렬 설명에 쓴다 — 「…카드는 등록하지 않아도 됩니다. / BRAND 플랜 기능을 그대로 쓰고, …」(요금 화면 체험 구역)
 * ⚠️ 한 문장이 칸보다 길면 그 덩이 안에서 다시 접힌다 — 긴 문장에는 쓰지 않는다.
 */
export const PUBLIC_SENTENCE_SX = { display: { xs: 'inline', md: 'inline-block' } } as const;

/**
 * **가운뎃점(·) 앞에서 줄이 갈리지 않게** — 「카피 / ·CTA」처럼 점으로 시작하는 줄을 막는다(점 뒤에서는 갈릴 수 있다).
 * 🔑 붙여 쓴 나열(「카피·CTA·랜딩」)이 있는 설명 글에 건다 — 낱말 사이 띄어쓰기는 그대로라 줄 꼴은 `balance` 가 정한다.
 * @param text 화면에 그릴 글
 */
export function glueDots(text: string): string {
  return text.replace(/·/g, '\u2060·');
}

/**
 * **붙여 쓴 나열·범위를 한 낱말로** — 「글·광고·상품을」·「1만~3만」 가운데서 줄이 갈리지 않게 가운뎃점·물결 양쪽을 붙인다.
 * 🔑 장면 제목·설명처럼 폰 폭에서 두세 줄로 접히는 짧은 글에 건다 — 줄은 띄어쓰기에서만 갈린다.
 * @param text 화면에 그릴 글
 */
export function glueWords(text: string): string {
  return text.replace(/([·~])/g, '\u2060$1\u2060');
}

/**
 * **「 · 」·「 — 」로 이은 짧은 이름은 한 덩이로** — 「판매몰 / 상품」·「시딩 전 / 체크」처럼 이름 가운데서 줄이 갈리지 않게,
 * 조각 안의 띄어쓰기를 붙는 공백으로 바꾼다. 줄은 구분자 뒤에서만 갈린다(구분자 앞은 붙여 「· 이벤트」로 시작하는 줄도 없다).
 * ⚠️ `maxLength` 를 넘는 조각은 그대로 둔다 — 좁은 칸에서 한 덩이가 칸을 뚫는다.
 * @param text 「타겟 멘션 추적 · 시딩 전 체크」 같은 나열
 * @param maxLength 한 덩이로 묶을 조각의 최대 글자 수(기본 10 — 카드 목록 폭 기준)
 */
export function keepPhrases(text: string, maxLength = 10): string {
  return text
    .split(/( · | — )/)
    .map((part) => {
      if (part === ' · ' || part === ' — ') return `\u00A0${part.trimStart()}`;
      return part.length > maxLength ? part : part.replace(/ /g, '\u00A0');
    })
    .join('');
}

/**
 * **칩 줄을 고르게** — 칩 N개를 줄마다 같은 수로 나눠 세운다(8개가 6+2 가 아니라 4+4).
 * @param count 칩 수
 * @param perRow 한 줄에 둘 수 있는 최대 수 — 폰·넓은 화면 따로
 * @returns 격자 sx — 칸은 그 열에서 가장 넓은 칩에 맞고, 칩은 칸을 채운다(`& > *` 폭 100%)
 */
export function balancedChipGridSx(count: number, perRow: { xs: number; md: number }) {
  const columnsOf = (max: number) => Math.ceil(count / Math.ceil(count / Math.max(1, max)));
  return {
    display: 'grid',
    gridTemplateColumns: { xs: `repeat(${columnsOf(perRow.xs)}, auto)`, md: `repeat(${columnsOf(perRow.md)}, auto)` },
    justifyContent: 'center',
    '& > *': { textAlign: 'center' },
  } as const;
}

/**
 * **공개 지면의 CTA 한 꼴** — 홈·기능 소개·요금이 함께 쓴다.
 *
 * 🚨 **높이를 여기서 준다** — 앱 테마의 단추 높이 사다리(`small` 28 · `medium` 32 · `large` 40)가 공개
 *   지면에도 걸린다. 조밀한 표를 위한 치수라 큰 제목 아래 CTA 가 눌려 보인다. `py` 로는 못 키운다 —
 *   사다리가 주는 것은 `height` 다.
 * ⚠️ 색은 싣지 않는다 — 파랑 채움 · 흰 채움 · 테두리 셋이 자리마다 다르다.
 */
export const PUBLIC_CTA_SX = {
  height: { xs: 52, md: 56 },
  px: { xs: 3, md: 4 },
  fontSize: 16,
  fontWeight: 600,
  borderRadius: RADIUS.md,
} as const;

/** 구역 이동 알약 — CTA 보다 한 칸 작다 */
export const PUBLIC_NAV_SX = {
  height: 36,
  px: 2,
  minWidth: 0,
  fontSize: 14,
  fontWeight: 600,
  borderRadius: RADIUS.pill,
} as const;

/** 공개 지면의 «떠 있는 판»(목업 틀) 그림자 — 하나만 쓴다 */
export const PUBLIC_FRAME_SHADOW = '0 24px 60px rgba(15, 23, 42, 0.10)';

/**
 * **장면이 살짝 떠오르는 움직임** — 들어오기 전에는 조금 아래에 있다가 제자리로 온다.
 * 🚨 흐리게(opacity 0) 기다리지 않는다 — 움직임이 안 돌아도(느린 기기 · 캡처) 내용은 처음부터 보인다.
 * @param shown 화면에 들어왔나(`useHomeReveal`)
 * @param reduceMotion 움직임을 줄이라는 설정인가 — 참이면 아무 움직임도 싣지 않는다
 */
export function revealSx(shown: boolean, reduceMotion: boolean) {
  if (reduceMotion) return {};
  return {
    transform: shown ? 'none' : 'translateY(24px)',
    transition: 'transform 0.7s cubic-bezier(0.2, 0.7, 0.2, 1)',
  } as const;
}
